import {
  AvailabilityDiagnosticTableData,
  DiagnosticRawSlotRow,
  DiagnosticSeatingAreaMapping,
  RawSlotReason,
  RawSlotStatus,
} from '@models/Availability'
import LoyaltyTier from '@components/loyaltyTier/LoyaltyTier'
import {
  Alert,
  Card,
  Collapse,
  Flex,
  Space,
  Table,
  TableColumnsType,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { FC } from 'react'

const { Text } = Typography

const REASON_LABELS: Record<Exclude<RawSlotReason, null>, string> = {
  not_bookable:
    'Sevenrooms returned this as a request-only slot (type ≠ "book").',
  tier_filtered:
    'Hidden for this tier — the slot carries a loyalty reservation tag this tier is not part of.',
  no_seating_area_mapping:
    'None of the access rule’s Sevenrooms seating areas map to a local seating area, and the integration hides unmapped slots.',
}

const STATUS_COLOR: Record<RawSlotStatus, string> = {
  shown: 'green',
  merged: 'blue',
  dropped: 'red',
}

const DispositionTag: FC<{
  status?: RawSlotStatus
  reason?: RawSlotReason
}> = ({ status, reason }) => {
  if (!status) {
    return <Text type="secondary">—</Text>
  }

  const tag = <Tag color={STATUS_COLOR[status]}>{status}</Tag>

  if (status === 'dropped' && reason) {
    return <Tooltip title={REASON_LABELS[reason]}>{tag}</Tooltip>
  }

  return tag
}

const TagList: FC<{ values: string[] }> = ({ values }) =>
  values.length ? (
    <Space size={[0, 4]} wrap>
      {values.map((value) => (
        <Tag key={value}>{value}</Tag>
      ))}
    </Space>
  ) : (
    <Text type="secondary">—</Text>
  )

const AvailabilityViewerDiagnostic: FC<{
  data: AvailabilityDiagnosticTableData
}> = ({ data }) => {
  if (data.error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Diagnostic could not be built"
        description={data.error}
      />
    )
  }

  const mappingColumns: TableColumnsType<DiagnosticSeatingAreaMapping> = [
    {
      title: 'Local seating area',
      key: 'name',
      render: (_, record) => (
        <Text>{record.name ?? `#${record.seatingAreaId}`}</Text>
      ),
    },
    {
      title: 'Sevenrooms seating area id',
      key: 'sevenroomsSeatingAreaId',
      render: (_, record) =>
        record.sevenroomsSeatingAreaId ? (
          <Text copyable>{record.sevenroomsSeatingAreaId}</Text>
        ) : (
          <Tag color="red">Not mapped</Tag>
        ),
    },
    {
      title: 'App booking enabled',
      key: 'appBookingEnable',
      width: 160,
      render: (_, record) => (
        <Tag color={record.appBookingEnable ? 'green' : 'default'}>
          {record.appBookingEnable ? 'Yes' : 'No'}
        </Tag>
      ),
    },
  ]

  const rawSlotColumns: TableColumnsType<DiagnosticRawSlotRow> = [
    {
      title: 'Time',
      key: 'time',
      fixed: 'left',
      width: 80,
      render: (_, record) => (
        <Text>{record.time || dayjs(record.timeIso).format('HH:mm')}</Text>
      ),
    },
    {
      title: 'Shift',
      key: 'shiftName',
      width: 120,
      render: (_, record) => (
        <Text type="secondary">{record.shiftName ?? '—'}</Text>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      width: 90,
      render: (_, record) => <Tag>{record.type}</Tag>,
    },
    {
      title: 'Access rule',
      key: 'accessRulePersistentId',
      width: 160,
      render: (_, record) =>
        record.accessRulePersistentId ? (
          <Text copyable ellipsis style={{ maxWidth: 140 }}>
            {record.accessRulePersistentId}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: '7R seating areas',
      key: 'seatingAreaIds',
      width: 200,
      render: (_, record) => <TagList values={record.seatingAreaIds} />,
    },
    {
      title: 'Reservation tags',
      key: 'reservationTags',
      width: 200,
      render: (_, record) => (
        <TagList values={record.reservationTags.map((tag) => tag.name)} />
      ),
    },
    ...data.tiers.map((tier) => ({
      title: <LoyaltyTier.Tag model={tier} />,
      key: `tier-${tier.id}`,
      width: 110,
      align: 'center' as const,
      render: (_: unknown, record: DiagnosticRawSlotRow) => {
        const disposition = record.dispositionByTier[tier.id]
        return (
          <DispositionTag
            status={disposition?.status}
            reason={disposition?.reason}
          />
        )
      },
    })),
  ]

  return (
    <Flex vertical gap="middle">
      <Alert
        type={data.displaySlotsNotAttachedToSeatingArea ? 'warning' : 'info'}
        showIcon
        message="Sevenrooms diagnostic"
        description={
          <Text>
            Shows every raw slot Sevenrooms returned and why it does or does not
            appear in Pléthore.{' '}
            <Text strong>Display slots not attached to a seating area</Text> is{' '}
            <Tag
              color={
                data.displaySlotsNotAttachedToSeatingArea ? 'orange' : 'default'
              }
            >
              {data.displaySlotsNotAttachedToSeatingArea ? 'ON' : 'OFF'}
            </Tag>
            for this venue.
          </Text>
        }
      />

      <Card title="Seating-area mapping" size="small">
        <Table
          rowKey={(record) => String(record.seatingAreaId)}
          bordered
          size="small"
          pagination={false}
          columns={mappingColumns}
          dataSource={data.seatingAreaMappings}
        />
      </Card>

      <Card title="Raw Sevenrooms slots" size="small">
        <Table
          rowKey="key"
          bordered
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          columns={rawSlotColumns}
          dataSource={data.rows}
        />
      </Card>

      {(data.rawResponse !== undefined ||
        data.request ||
        data.httpRequest) && (
        <Collapse
          items={[
            ...(data.httpRequest
              ? [
                  {
                    key: 'http',
                    label: 'Outbound HTTP request (for Sevenrooms support)',
                    children: (
                      <Flex vertical gap="small">
                        <Alert
                          type="info"
                          showIcon
                          message="Exact request sent to Sevenrooms when computing this diagnostic. The Authorization header is redacted — safe to copy-paste into a support ticket."
                        />
                        <Space size={[8, 4]} wrap>
                          <Tag color="blue">
                            <Text strong>{data.httpRequest.method}</Text>
                          </Tag>
                          <Tag>
                            sent_at:{' '}
                            <Text strong>{data.httpRequest.sentAt}</Text>
                          </Tag>
                        </Space>
                        <Text
                          copyable={{ text: data.httpRequest.url }}
                          style={{ fontFamily: 'monospace', fontSize: 12 }}
                        >
                          {data.httpRequest.url}
                        </Text>
                        <Text copyable={{ text: JSON.stringify(data.httpRequest, null, 2) }}>
                          Copy full request JSON
                        </Text>
                        <pre
                          style={{
                            maxHeight: 320,
                            overflow: 'auto',
                            background: '#0f172a',
                            color: '#e2e8f0',
                            padding: 12,
                            borderRadius: 6,
                            fontSize: 12,
                            margin: 0,
                          }}
                        >
                          {JSON.stringify(data.httpRequest, null, 2)}
                        </pre>
                      </Flex>
                    ),
                  },
                ]
              : []),
            {
              key: 'raw',
              label: 'Raw Sevenrooms API response',
              children: (
                <Flex vertical gap="small">
                  <Alert
                    type="info"
                    showIcon
                    message="From the “Default” (no-tier) request. The Sevenrooms availability call does not depend on the loyalty tier — tiers are applied by Pléthore afterwards — so this payload is the same for every tier; only the disposition columns above differ."
                  />
                  {data.request && (
                    <Space size={[8, 4]} wrap>
                      {Object.entries(data.request).map(([key, value]) => (
                        <Tag key={key}>
                          {key}: <Text strong>{String(value)}</Text>
                        </Tag>
                      ))}
                    </Space>
                  )}
                  <Text copyable={{ text: JSON.stringify(data.rawResponse) }}>
                    Copy raw JSON
                  </Text>
                  <pre
                    style={{
                      maxHeight: 480,
                      overflow: 'auto',
                      background: '#0f172a',
                      color: '#e2e8f0',
                      padding: 12,
                      borderRadius: 6,
                      fontSize: 12,
                      margin: 0,
                    }}
                  >
                    {JSON.stringify(data.rawResponse, null, 2)}
                  </pre>
                </Flex>
              ),
            },
          ]}
        />
      )}
    </Flex>
  )
}

type AvailabilityViewerDiagnosticType = {
  Diagnostic: typeof AvailabilityViewerDiagnostic
}

export { AvailabilityViewerDiagnostic, type AvailabilityViewerDiagnosticType }
